import React, { useState, useMemo } from "react";
import { styled } from "@mui/styles";
import { connect } from "react-redux";
import { darken } from "polished";
import { useHistory } from "react-router-dom";
import { bindActionCreators } from "redux";
import { actionCreators } from "../redux/reducers/caseReducer";
import UserTimerHOC from "./userTimer/UserTimerHOC";
import { useAuth } from "../contexts/authContext";
import { setIsSearching } from "../redux/actions/caseActions";
import caseNestLogo from "../images/CaseNest_Purple_Positive_RGB (1).svg";
import {
    Grid,
    Button,
    InputBase,
    Menu,
    MenuItem,
    AppBar as MuiAppBar,
    IconButton as MuiIconButton,
    Toolbar,
    InputAdornment,
    CircularProgress,
    Typography,
    Alert,
    AlertTitle,
} from "@mui/material";
import {
    Menu as MenuIcon,
    ChevronLeft as HideMenuIcon
} from "@mui/icons-material";
import { Search as SearchIcon, Power, ArrowRightCircle } from "react-feather";
import CaseActivityTimer from "./CaseActivityTimer";
import HeldTimeButton from "./HeldTimeButton";
import useUserAutomaticEmailReply from "../hooks/queries/useUserAutomaticEmailReply";
import useUpdateUserAutomaticEmailReply from "../hooks/mutations/useUpdateUserAutomaticEmailReply";

const AppBar = styled(MuiAppBar)(({ theme }) => ({
    background: theme.header.background,
    color: theme.header.color,
    boxShadow: theme.shadows[1],
}));

const IconButton = styled(MuiIconButton)({
    '& svg': {
        width: '22px',
        height: '22px',
    }
});

const Search = styled('div')(({ theme }) => ({
    borderRadius: '2px',
    backgroundColor: theme.header.background,
    display: 'none',
    position: 'relative',
    width: '100%',
    '&:hover': {
        backgroundColor: darken(0.05, theme.header.background)
    },
    [theme.breakpoints.up("lg")]: {
        display: 'block'
    },
}));


const SearchIconWrapper = styled('div')({
    width: '50px',
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
        width: '22px',
        height: '22px',
    }
});

const Input = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    width: '100%',

    '&> input': {
        color: theme.header.search.color,
        paddingTop: theme.spacing(2.5),
        paddingRight: theme.spacing(2.5),
        paddingBottom: theme.spacing(2.5),
        paddingLeft: theme.spacing(12),
        width: '160px',
    }
}));


const UserMenu = () => {
    const { logout } = useAuth();

    const [anchorMenu, setAnchorMenu] = useState(null);

    const toggleMenu = (event) => {
        setAnchorMenu(event.currentTarget);
    };

    const closeMenu = () => {
        setAnchorMenu(null);
    };

    const open = !!anchorMenu;

    return (
        (<React.Fragment>
            <IconButton
                aria-owns={open ? "menu-appbar" : undefined}
                aria-haspopup="true"
                onClick={toggleMenu}
                color="inherit"
                size="large">
                <Power />
            </IconButton>
            <Menu id="menu-appbar" anchorEl={anchorMenu} open={open} onClose={closeMenu}>
                <MenuItem onClick={logout}>Logout</MenuItem>
            </Menu>
        </React.Fragment>)
    );
};

const Header = ({ onDrawerToggle, getCase, caseState, setIsSearching, drawerOpen, userId, isMinimal = false }) => {
    const [caseNum, setCaseNum] = useState("");

    const history = useHistory();

    const { data: reply } = useUserAutomaticEmailReply({ userId });
    const updateReply = useUpdateUserAutomaticEmailReply();

    const [updatingReply, setUpdatingReply] = useState();

    const turnOffReplies = () => {
        setUpdatingReply(true);
        updateReply.mutate(
            {
                userId,
                content: reply.content,
                fromDate: reply.fromDate,
                toDate: reply.toDate,
                isActive: false,
            },
            {
                onError: () => setUpdatingReply(false),
                onSuccess: () => setUpdatingReply(false),
            }
        );
    }

    const searchCase = async () => {
        if (caseNum === "")
            return;

        setIsSearching(true);

        await getCase(caseNum, history);
        setIsSearching(false);
    };

    const handleEnterKeyPress = (e) => {
        if (e.key === "Enter") {
            searchCase();
        }
    };

    const showReplyBanner = useMemo(() => {

        if (isMinimal || !reply?.isActive)
            return false;

        if (!reply.fromDate || !reply.toDate)
            return true;

        let now = new Date();
        let from = new Date(reply.fromDate);
        let to = new Date(reply.toDate);

        return now > from && now < to;

    }, [isMinimal, reply]);

    return (
        (<React.Fragment>
            <AppBar position="sticky" elevation={0}>
                {
                    showReplyBanner &&
                    <Alert severity="warning"
                        action={
                            <React.Fragment>
                                <Button
                                    variant="outlined"
                                    style={{ backgroundColor: 'white', marginRight: '10px' }}
                                    onClick={() => history.push("/admin/automaticreplies")}
                                >
                                    Automatic Replies
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={turnOffReplies}
                                    disabled={updatingReply}
                                >
                                    {updatingReply ? "Updating .." : "Turn Off"}
                                </Button>
                            </React.Fragment>
                        }
                    >
                        <AlertTitle>Automatic replies are on.</AlertTitle>
                    </Alert>
                }
                <Toolbar>
                    <Grid container alignItems="center" spacing={2}>

                        {isMinimal && (
                            <>
                                <Grid item>
                                    <img src={caseNestLogo} alt="CaseNest Logo" style={{ height: "48px" }} />
                                </Grid>
                                <Grid item xs />
                            </>
                        )}
                        {!isMinimal && (
                            <>
                                <Grid item xs={12}>

                                </Grid>
                                <Grid item>
                                    <IconButton
                                        color="inherit"
                                        aria-label="Open drawer"
                                        onClick={onDrawerToggle}
                                        size="large">
                                        {drawerOpen ? <MenuIcon /> : <HideMenuIcon />}
                                    </IconButton>
                                </Grid>
                                <Grid item>
                                    <Search>
                                        <SearchIconWrapper>
                                            <SearchIcon />
                                        </SearchIconWrapper>
                                        <Input
                                            data-cy="search_cases"
                                            value={caseNum}
                                            onChange={(e) => setCaseNum(e.target.value)}
                                            onKeyPress={handleEnterKeyPress}
                                            placeholder="Search case"
                                            type="number"
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton onClick={searchCase} size="large">
                                                        <ArrowRightCircle />
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                        />
                                    </Search>
                                </Grid>
                                {caseState.isSearching && (
                                    <Grid item>
                                        <CircularProgress size="1.5em" />
                                    </Grid>
                                )}
                                <Grid item>
                                    <UserTimerHOC />
                                </Grid>
                                <Grid item>
                                    <HeldTimeButton />
                                </Grid>
                                <Grid item xs />
                                <Grid item style={{ marginRight: "16px" }}>
                                    <Typography variant="h6">{caseState && caseState.currentCaseId ? "Case " + caseState.currentCaseId : ""}</Typography>
                                </Grid>
                                <Grid item>
                                    <Typography variant="h6">
                                        <CaseActivityTimer />
                                    </Typography>
                                </Grid>
                            </>
                        )}
                        <Grid item>
                            <UserMenu />
                        </Grid>
                    </Grid>
                </Toolbar>
            </AppBar>
        </React.Fragment>)
    );
};

const mapStateToProps = (state) => ({
    caseState: state.caseReducer,
    userId: state.userReducer.userProfile?.userId
});

const mapDispatchToProps = (dispatch) => {
    return {
        ...bindActionCreators(actionCreators, dispatch),
        setIsSearching: (isSearching) => dispatch(setIsSearching(isSearching)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);
